import React, { PropsWithChildren } from 'react'
import { Flex, Text, Icon, Button } from '@workwhile/ui'
import { Link } from 'react-router-dom'
import { ColumnDef } from '@tanstack/react-table'
import { useDayforceJobsIssuesContextValue } from '../context/DayforceJobsIssuesProvider'
import { formatInTimeZone } from 'date-fns-tz'
import { ExternalLinkIcon } from 'lucide-react'
import * as ROUTES from 'constants/routes'
import { DayforceJobsIssuesItems } from 'api/admin/dayforce/listDayforceJobsIssues'
import { DAYFORCE_SHIFTS_DEFAULT_TIMEZONE } from 'pages/Dayforce/constants'
import { PaginatedTable } from 'components/Table/PaginatedTable'

const DayforceJobButton = ({
  agencyId,
  jobId,
  children,
}: PropsWithChildren<{ agencyId: string; jobId: string }>) => {
  const {
    state: { agencies },
  } = useDayforceJobsIssuesContextValue()

  const agencyBaseUrl = agencies?.[agencyId]?.dayforceBaseUrl

  if (!agencyBaseUrl) {
    return null
  }

  return (
    <Button
      kind="text"
      as="a"
      href={`${agencyBaseUrl}/jobs/${jobId}`}
      target="_blank"
      rel="noopener noreferrer"
      style={{ textDecoration: 'none', color: 'inherit' }}
      variant="secondary"
    >
      <Flex alignItems="center" gap={2}>
        {children}
        <Icon icon={ExternalLinkIcon} size={12} />
      </Flex>
    </Button>
  )
}

const JobDetailsButton = ({
  agencyId,
  jobId,
  children,
}: PropsWithChildren<{ agencyId: string; jobId: string }>) => (
  <Button
    as={Link}
    to={ROUTES.DAYFORCE_JOB_DETAIL.replace(':agencyId', agencyId).replace(
      ':jobId',
      jobId
    )}
    kind="text"
    variant="secondary"
  >
    {children}
  </Button>
)

const ShiftDetailsButton = ({ shiftId }: { shiftId: number }) => (
  <Button
    as={Link}
    to={ROUTES.SHIFT_DETAIL.replace(':shiftId', shiftId.toString())}
    kind="text"
    variant="secondary"
  >
    View shift
  </Button>
)

const AgencyCell = ({ agencyId }: { agencyId: string }) => {
  const {
    state: { agencies },
  } = useDayforceJobsIssuesContextValue()

  return (
    <Text style={{ whiteSpace: 'nowrap' }}>
      {agencies?.[agencyId]?.name || agencyId}
    </Text>
  )
}

const columns: ColumnDef<DayforceJobsIssuesItems[number]>[] = [
  {
    header: 'Agency',
    accessorKey: 'agencyId',
    cell: ({ row }) => <AgencyCell agencyId={row.original.agencyId} />,
  },
  {
    header: 'Company',
    accessorKey: 'company.name',
    cell: ({ row }) => (
      <Text>
        {row.original.company.name} ({row.original.company.id})
      </Text>
    ),
  },
  {
    header: 'Job ID',
    accessorKey: 'jobId',
    cell: ({ row }) => <Text>{row.original.jobId}</Text>,
  },
  {
    header: 'Starts At',
    accessorKey: 'startsAt',
    cell: ({ row }) => {
      const startsAt = formatInTimeZone(
        row.original.startsAt,
        DAYFORCE_SHIFTS_DEFAULT_TIMEZONE,
        'EEEE, MMMM d, h:mm a z'
      )
      return <Text>{startsAt}</Text>
    },
  },
  {
    header: 'Event',
    cell: ({ row }) => {
      return (
        <Text style={{ whiteSpace: 'nowrap' }}>
          {row.original.issues.join(', ')}
        </Text>
      )
    },
  },
  {
    header: 'Actions',
    cell: ({ row }) => {
      return (
        <Flex gap={2} flexWrap="wrap">
          <DayforceJobButton
            agencyId={row.original.agencyId}
            jobId={row.original.jobId}
          >
            View job
          </DayforceJobButton>
          <JobDetailsButton
            agencyId={row.original.agencyId}
            jobId={row.original.jobId}
          >
            View details
          </JobDetailsButton>
          {row.original.shiftId ? (
            <ShiftDetailsButton shiftId={row.original.shiftId} />
          ) : null}
        </Flex>
      )
    },
  },
]

export const DayforceJobsIssuesTable = () => {
  const {
    state: { data, pagination, isFetching },
    actions: { setPagination },
  } = useDayforceJobsIssuesContextValue()

  return (
    <PaginatedTable
      columns={columns}
      items={data.items}
      rowCount={data.totalRows}
      pagination={pagination}
      setPagination={setPagination}
      isFetching={isFetching}
      emptyMessage="No data found."
    />
  )
}
